import { BASE_URL } from "@/config";
import axios from "axios";
import { CalendarEvent, CalendarLocation, CalendarPartner, CalendarVehicle } from "./calendar-types";
import { mergeDateAndTime } from "@/utils/formChange";
import axiosInstance from "@/utils/axiosInstance";
import { Reservation } from "@/interface/entityInterface";

const getReservationStates = () => {
    return [
        { value: 'pending', label: 'Pending', color: 'blue' },
        { value: 'approved', label: 'Approved', color: 'green' },
        { value: 'rejected', label: 'Rejected', color: 'red' },
    ];
}

const getLocations = async () => {
    let locations: CalendarLocation[] = [];
    var response = await axios.get(`${BASE_URL}/location/device`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem("access_token")}`,
        },
        data: {
            start: 1,
            end: 10000,
        }
    });

    if (Array.isArray(response?.data)) {
        locations = response.data.map((location: any) => ({
            id: location.id,
            name: `${location.address}, ${location.city}, ${location.state}`,
        }));
    }
    else {
        console.error("Unexpected getLocations format:", response);
    }

    return locations;
};

const getCommissionPartners = async () => {

    let partners: CalendarPartner[] = [];
    const response = await axios.get(`${BASE_URL}/referral-agents`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem("access_token")}`,
        },
    });

    if (Array.isArray(response?.data)) {
        partners = response.data.map((partner: any) => ({
            id: partner.id,
            name: `${partner.name} (${partner.percentage_commission}%)`,
            commission: partner.percentage_commission,
        }));
    }
    else {
        console.error("Unexpected getCommissionPartners format:", response);
    }

    return partners;
};

const getVehicles = async () => {
    let vehicles: CalendarVehicle[] = [];

    const response = await axios.get(`${BASE_URL}/vehicles`, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem("access_token")}`,
        },
    });

    if (Array.isArray(response?.data)) {
        vehicles = response.data.map((vehicle: any) => ({
            id: vehicle.id,
            name: (`${vehicle.make.name} - ${vehicle.license_plate} `) + (vehicle.nickname ? `(${vehicle.nickname})` : ''),
        }));
    }
    else {
        console.error("Unexpected response format:", response);
    }

    return vehicles;
};

const getReservations = async () => {
    let events: CalendarEvent[] = [];
    const response = await axiosInstance.get(`/auto-service-reservation`);

    if (Array.isArray(response?.data)) {
        events = response.data.map((reservation: Reservation) => ({
            ...reservation,
            id: "" + reservation.id,
            title: (`${reservation?.guest_user?.name} - ${reservation?.guest_vehicle?.license_plate}`),
            start: new Date(reservation.date),
            end: new Date(reservation.date),
            color: "black"
        }));
    } else {
        console.error("Unexpected response format for reservations:", response.data);
    }

    return events;
};

const addReservation = async (newEvent: any) => {
    var reservationData = {
        pickup_date: newEvent.start.toISOString(),
        dropoff_date: newEvent.end.toISOString(),
        vehicle_id: newEvent.vehicleId,
        commission_partner_id: newEvent.partnerId || 0, 
        pickup_location_id: newEvent.pickupId,
        dropoff_location_id: newEvent.dropoffId,
        full_name: newEvent.customerName,
        email: newEvent.customerEmail,
        phone_number: newEvent.customerPhone,
        amount: newEvent.price,
        discount: newEvent.discount,
        discount_type: newEvent.discountType,
        status: newEvent.status,
        notes: newEvent.notes,
    }

    return await axios.post(`${BASE_URL}/reservations/device/add-booking`, reservationData, {
        headers: {
            'Authorization': `Bearer ${localStorage.getItem("access_token")}`,
            'Content-Type': 'application/json',
        },
    });
}

const updateReservation = async (newEvent: CalendarEvent) => {

    var updateReservation = {
        pickup_date: newEvent.start.toISOString(),
        dropoff_date: newEvent.end.toISOString(),
        vehicle_id: newEvent.vehicleId,
        commission_partner_id: newEvent.partnerId || 0, // Api requires to set an int value for partner
        pickup_location_id: newEvent.pickupId,
        dropoff_location_id: newEvent.dropoffId,
        full_name: newEvent.customerName,
        email: newEvent.customerEmail,
        phone_number: newEvent.customerPhone,
        amount: newEvent.price,
        discount: newEvent.discount,
        discount_type: "percentage",
        status: newEvent.status,
        notes: newEvent.notes,
    };

    // TODO: Implement update reservation API call
    throw new Error("Update reservation API not implemented yet");
}

const deleteReservation = async (reservationId: string) => {

    throw new Error("Delete reservation API not implemented yet");
}

export {
    getLocations,
    getCommissionPartners,
    getVehicles,
    getReservations,
    getReservationStates,
    addReservation,
    updateReservation,
    deleteReservation
};